import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Filter, Search, Printer, PenTool, Layers as LayersIcon, AlertCircle, Clock, CheckCircle2, XCircle, CloudOff, RefreshCw, LayoutGrid, Kanban } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { CreateWorkOrderDialog } from "@/components/WorkOrders/CreateWorkOrderDialog";
import { WorkOrderDetails } from "@/components/WorkOrders/WorkOrderDetails";
import { WorkOrderKanbanView } from "@/components/WorkOrders/WorkOrderKanbanView";
import { Progress } from "@/components/ui/progress";
import { useWorkOrdersOffline } from "@/hooks/useWorkOrdersOffline";
import { useSyncState } from "@/hooks/useSyncState";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useSearchParams } from "react-router-dom";

export default function WorkOrders() {
  const { t, language } = useLanguage();
  const { userRole } = useAuth();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const canManage = userRole === 'admin' || userRole === 'supervisor';
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [orderTypeFilter, setOrderTypeFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<'grid' | 'kanban'>('grid');

  // Use offline-capable hook
  const { data: workOrders, isLoading, isOfflineData, refetch } = useWorkOrdersOffline();
  const { isOnline, pendingCount } = useSyncState();

  useEffect(() => {
    const query = searchParams.get("q");
    if (query !== null) {
      setSearchTerm(query);
    }
  }, [searchParams]);

  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) {
      setSelectedOrderId(openId);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("open");
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Mapeo de prioridad numérica a string para filtros
  const priorityNumToString: Record<number, string> = {
    1: "low",
    2: "normal",
    3: "high",
    4: "urgent",
  };

  // Filtrar órdenes
  const filteredOrders = workOrders?.filter((order: any) => {
    const matchesSearch = searchTerm === "" ||
      order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    const orderPriorityString = priorityNumToString[order.priority] || "normal";
    const matchesPriority = priorityFilter === "all" || orderPriorityString === priorityFilter;

    const matchesOrderType = orderTypeFilter === "all" || order.order_type === orderTypeFilter;
    return matchesSearch && matchesStatus && matchesPriority && matchesOrderType;
  });

  // Estadísticas
  const stats = {
    total: workOrders?.length || 0,
    pending: workOrders?.filter((o: any) => o.status === "pending").length || 0,
    inProgress: workOrders?.filter((o: any) => o.status === "in_progress").length || 0,
    completed: workOrders?.filter((o: any) => o.status === "completed").length || 0,
  };

  // Calcular progreso de una orden (basado en piezas si existen, sino en procesos)
  const getOrderProgress = (order: any) => {
    // Si tiene work_order_items, usar esos para el progreso
    const items = order.work_order_items || [];
    if (items.length > 0) {
      const completed = items.filter((i: any) => i.status === "completed").length;
      return {
        percent: Math.round((completed / items.length) * 100),
        completed,
        total: items.length,
        type: 'items',
      };
    }

    // Fallback a process_records
    const records = order.process_records || [];
    if (records.length === 0) {
      return { percent: 0, completed: 0, total: 0, type: 'none' };
    }
    const completed = records.filter((r: any) => r.status === "completed").length;
    return {
      percent: Math.round((completed / records.length) * 100),
      completed,
      total: records.length,
      type: 'processes',
    };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-500/10 text-green-500 border-green-500/20";
      case "in_progress":
        return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      case "cancelled":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      default:
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
    }
  };

  if (selectedOrderId) {
    return (
      <DashboardLayout>
        <WorkOrderDetails
          orderId={selectedOrderId}
          onBack={() => setSelectedOrderId(null)}
        />
      </DashboardLayout>
    );
  }

  // Obtener color de prioridad
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      case "high":
        return "bg-orange-500/10 text-orange-500 border-orange-500/20";
      case "normal":
        return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      case "low":
        return "bg-gray-500/10 text-gray-500 border-gray-500/20";
      default:
        return "bg-gray-500/10 text-gray-500 border-gray-500/20";
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Offline Banner */}
        {(isOfflineData || !isOnline) && (
          <Alert variant="default" className="bg-amber-500/10 border-amber-500/30">
            <CloudOff className="h-4 w-4 text-amber-600" />
            <AlertDescription className="flex items-center justify-between">
              <span className="text-amber-700 dark:text-amber-400">
                {!isOnline
                  ? "Sin conexión - mostrando datos guardados localmente"
                  : "Mostrando datos en caché"}
                {pendingCount > 0 && ` (${pendingCount} cambios pendientes)`}
              </span>
              {isOnline && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refetch()}
                  className="ml-2"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Actualizar
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {t.workOrders.title}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t.workOrders.subtitle}
            </p>
          </div>
          {canManage && (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t.workOrders.newOrder}
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Clock className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-sm text-muted-foreground">Pendientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Clock className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.inProgress}</p>
                  <p className="text-sm text-muted-foreground">En Progreso</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.completed}</p>
                  <p className="text-sm text-muted-foreground">Completadas</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* View Toggle + Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="rounded-none"
            >
              <LayoutGrid className="w-4 h-4 mr-2" />
              Grid
            </Button>
            <Button
              variant={viewMode === 'kanban' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('kanban')}
              className="rounded-none"
            >
              <Kanban className="w-4 h-4 mr-2" />
              Kanban
            </Button>
          </div>
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por número, producto o cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="pending">Pendiente</SelectItem>
              <SelectItem value="in_progress">En Progreso</SelectItem>
              <SelectItem value="completed">Completada</SelectItem>
              <SelectItem value="cancelled">Cancelada</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Prioridad" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las prioridades</SelectItem>
              <SelectItem value="urgent">Urgente</SelectItem>
              <SelectItem value="high">Alta</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="low">Baja</SelectItem>
            </SelectContent>
          </Select>
          <Select value={orderTypeFilter} onValueChange={setOrderTypeFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Tipo OT" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="plotter">Plotter</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="design_cnc">Diseño/CNC</SelectItem>
              <SelectItem value="mold">Molde</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t.common.loading}</p>
          </div>
        ) : filteredOrders && filteredOrders.length > 0 ? (
          viewMode === 'kanban' ? (
            <WorkOrderKanbanView
              workOrders={filteredOrders}
              onSelectOrder={(id) => setSelectedOrderId(id)}
            />
          ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredOrders.map((order: any) => {
              const progress = getOrderProgress(order);
              const priorityString = priorityNumToString[order.priority] || "normal";

              return (
                <Card
                  key={order.id}
                  className="cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => setSelectedOrderId(order.id)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <CardTitle className="text-lg flex items-center gap-2">
                          {order.order_number}
                          {order._pendingSync && (
                            <CloudOff className="w-4 h-4 text-amber-500" title="Pendiente de sincronizar" />
                          )}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground truncate">
                          {order.product_name}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={getStatusColor(order.status)}>
                          {t.statuses[order.status as keyof typeof t.statuses] || order.status}
                        </Badge>
                        {priorityString && priorityString !== "normal" && (
                          <Badge variant="outline" className={getPriorityColor(priorityString)}>
                            {priorityString === "urgent" ? "Urgente" :
                             priorityString === "high" ? "Alta" : "Baja"}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {order.customer && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">Cliente:</span>
                        <span className="font-medium">{order.customer}</span>
                      </div>
                    )}

                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Cantidad:</span>
                      <span className="font-medium">{order.quantity} unidades</span>
                    </div>

                    {/* Progress Bar */}
                    {(progress.type === 'items' || progress.type === 'processes') && (
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">
                            {progress.type === 'items' ? 'Piezas' : 'Procesos'}
                          </span>
                          <span className="font-medium">
                            {progress.completed}/{progress.total} ({progress.percent}%)
                          </span>
                        </div>
                        <Progress value={progress.percent} className="h-2" />
                      </div>
                    )}

                    <div className="pt-2 border-t text-xs text-muted-foreground">
                      Creada: {format(new Date(order.created_at), "Pp", {
                        locale: language === "es" ? es : enUS,
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          )
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <XCircle className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-2">
                {searchTerm || statusFilter !== "all" || priorityFilter !== "all"
                  ? "No se encontraron órdenes con los filtros seleccionados"
                  : t.workOrders.noOrders}
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                {t.workOrders.noOrdersDescription}
              </p>
              {canManage && (
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t.workOrders.createOrder}
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <CreateWorkOrderDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </DashboardLayout>
  );
}
